// ─────────────────────────────────────────────
//  MessagingTopbarButton — the app-wide topbar entry to messaging (D-01 /
//  UI-SPEC "Topbar Messaging Entry + Unread Badge"). Mirrors BoardAlertControls'
//  topbar-control styling + dropdown semantics, but opens the full MessagingPanel
//  (a dialog) rather than an anchored menu.
//
//  • Label "💬 Messages" on desktop; icon-only "💬" under 480px — but ALWAYS
//    with an accessible name. Secondary control style (NOT the navy fill — navy
//    is reserved for the unread badge).
//  • Reads the live `unread` total from useMessaging(); renders the numbered
//    UnreadBadge top-right (hidden at 0, "99+" cap). The accessible name updates
//    to "Messages, N unread".
//  • aria-haspopup="dialog", aria-expanded={open}; toggling open mounts/unmounts
//    MessagingPanel (which traps focus, closes on Escape, restores focus here).
//  • First interaction calls unlockAudioOnFirstGesture() so the chime can play
//    later (autoplay-safe, D-03).
//
//  Mounted once in the app shell (App.js) so it is present on every page and
//  shares the single MessagingProvider subscription set.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useMessaging } from "../context/MessagingContext";
import { unlockAudioOnFirstGesture } from "../audio/chime";
import { useIsMobile } from "../hooks/useIsMobile";
import UnreadBadge from "./UnreadBadge";
import ChimeMuteToggle from "./ChimeMuteToggle";
import MessagingPanel from "./MessagingPanel";
import tokens from "../theme/tokens";

const { color, radius, font, space } = tokens;

export default function MessagingTopbarButton() {
  const { unread } = useMessaging();
  const [open, setOpen] = useState(false);

  // A room card elsewhere can ask for the panel. A window event rather than a
  // context method on purpose: this component already owns the open state, and
  // threading a setter through the provider would give every consumer the power
  // to open the panel when only a couple of buttons need it.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("atc-open-messages", onOpen);
    return () => window.removeEventListener("atc-open-messages", onOpen);
  }, []);
  // Icon-only under 480px (still labelled); full "💬 Messages" above.
  const iconOnly = useIsMobile(480);

  const count = Number.isFinite(unread) ? unread : 0;
  // Accessible name always carries the count (UI-SPEC: "Messages, N unread").
  const accessibleName = count > 0 ? `Messages, ${count} unread` : "Messages";

  const handleOpen = () => {
    // First gesture unlocks audio so an incoming-message chime can play (D-03).
    unlockAudioOnFirstGesture();
    setOpen(true);
  };

  return (
    <>
      {/* Hide the floating launcher while the panel is open so it never overlaps
          the panel's composer / Send button (mobile full-screen + desktop drawer
          both cover the bottom-right FAB spot). display:none (not unmount) keeps
          the node so MessagingPanel can restore focus here on close. */}
      <div style={{ ...styles.anchor, display: open ? "none" : "inline-flex" }}>
        <button
          type="button"
          onClick={handleOpen}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={accessibleName}
          title="Messages"
          style={styles.btn}
        >
          <span aria-hidden="true">💬</span>
          {!iconOnly && <span style={styles.label}>Messages</span>}
        </button>
        {/* Numbered navy badge, top-right; hidden entirely at zero unread. */}
        <span style={styles.badgeWrap} aria-hidden="true">
          <UnreadBadge count={count} />
        </span>
      </div>

      {/* Screen-reader announcement of unread changes — the badge/aria-label
          alone are silent unless the button happens to be focused, and the
          chime is inaudible to deaf users. Polite so it never interrupts. */}
      <span aria-live="polite" style={styles.srOnly}>
        {count > 0 ? `${count} unread message${count === 1 ? "" : "s"}` : ""}
      </span>

      <MessagingPanel
        isOpen={open}
        onClose={() => setOpen(false)}
        headerSlot={<ChimeMuteToggle />}
      />
    </>
  );
}

const styles = {
  anchor: {
    position: "relative",
    display: "inline-flex",
  },
  // Secondary control — NOT the navy fill (reserved for the badge). Mirrors the
  // BoardAlertControls button sizing (9px 14px / body-14 / bold / radius.sm).
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: space.xs,
    minHeight: 44, // touch target (UI-SPEC)
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    padding: "9px 14px",
    fontSize: font.size.body, // 14
    fontWeight: font.weight.bold, // 700
    color: color.text.secondary,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  label: { lineHeight: 1 },
  srOnly: { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 },
  // Position the badge top-right of the button; UnreadBadge renders nothing at 0.
  badgeWrap: {
    position: "absolute",
    top: -6,
    right: -6,
    pointerEvents: "none",
  },
};
